import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactNode, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { authClient } from '@/shared/api';
import { paths } from '@/shared/lib/paths';
import { Form } from '@/shared/ui/form';
import { Button } from '@/shared/ui/primitives/button';
import { authErrorMessage } from '../components/auth-errors';
import {
  type ForgotPasswordFormValues,
  forgotPasswordFormSchema,
} from '../components/auth-form-schemas';
import { AuthPageLayout } from '../components/auth-page-layout';
import { FormAlert } from '../components/form-alert';
import { TextField } from '../components/text-field';

/** AUTH-4: pede o link de redefinição. A resposta é a mesma exista ou não o e-mail. */
export function ForgotPasswordPage(): ReactNode {
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordFormSchema),
    defaultValues: { email: '' },
  });

  async function submit(values: ForgotPasswordFormValues): Promise<void> {
    setFailure(null);
    try {
      await authClient.requestPasswordReset(values);
      setSent(true);
    } catch (error) {
      setFailure(authErrorMessage(error, 'Não foi possível enviar o pedido. Tente de novo.'));
    }
  }

  if (sent) {
    return (
      <AuthPageLayout title="Confira seu e-mail">
        <p role="status">
          Se o e-mail estiver cadastrado, você vai receber um link para criar uma senha nova. O link
          vale por 1 hora e só pode ser usado uma vez.
        </p>
        <Link to={paths.login()} className="text-sm underline underline-offset-4">
          Voltar para o login
        </Link>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      title="Esqueci minha senha"
      description="Informe o e-mail da sua conta para receber um link de redefinição."
    >
      <Form {...form}>
        <form
          noValidate
          className="space-y-4"
          onSubmit={(event) => {
            void form.handleSubmit(submit)(event);
          }}
        >
          {failure !== null && <FormAlert>{failure}</FormAlert>}
          <TextField
            control={form.control}
            name="email"
            label="E-mail"
            type="email"
            autoComplete="email"
          />
          <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
            Enviar link
          </Button>
        </form>
      </Form>
      <Link to={paths.login()} className="text-sm underline underline-offset-4">
        Voltar para o login
      </Link>
    </AuthPageLayout>
  );
}
